/**
 * WHO OUT-RANKS A GLOBAL HOTKEY (th-juvxm).
 *
 * `vue-three-shortkey` installs ONE document-CAPTURE keydown/keyup listener at
 * module load, and on any registered chord it calls `preventDefault()` +
 * `stopPropagation()` before the event has reached anything below `document`.
 * A bare letter registered by ANY component is therefore stolen from EVERY
 * surface in the app: the basemap switcher (`BasemapSwitcher`) registers
 * `b`/`s`/`h`/`t` for the basemaps, so without this contract a held `s` meant
 * for the review pane's Skip dies at document capture and never reaches the
 * pane's own element-scoped listener — while `a` and `r`, which no hotkey
 * claims, work.
 *
 * The library's one stand-down hook is the `prevent` install option: CSS
 * selectors matched against `document.activeElement` with `.matches()`, so a
 * selector must cover descendants itself. This list IS that hook, and the one
 * place the app declares who out-ranks a global hotkey.
 *
 * A surface that runs its own keyboard opts in with ONE attribute on its root:
 *
 *   <div data-keyboard-surface tabindex="-1"> … </div>
 *
 * and every global hotkey — present and future — stands down while focus sits
 * on that root or anywhere inside it. Deliberately all-or-nothing per surface,
 * exactly as the text-entry selectors already are: a surface that owns the
 * keyboard owns all of it, chords included. The alternatives are both dead
 * ends — re-keying a surface's roster away from the stolen letter leaves the
 * next letter broken, and gating one hotkey on one store flag leaves the next
 * surface broken.
 *
 * THE SECOND CONSUMER (th-fxhic). `vue-three-shortkey` is not the app's only
 * document-CAPTURE key claimer: `useVoiceMode` installs its own pair for the
 * Shift + Space push-to-talk hold. It reads the declared-surface half of the
 * contract through `keyboardOwningSurfaceHasFocus()` rather than growing a
 * rule of its own — the halves are split here, and composed back into
 * `GLOBAL_HOTKEY_AVOID_SELECTORS`, so the attribute and its two selector forms
 * are still written exactly once. Only the declared-surface half applies to
 * voice mode: the text-entry selectors are the very targets push-to-talk
 * dictates INTO, so a voice handler that stood down on them would stand down
 * always. A text input that a declared surface CONTAINS is still the
 * surface's, via the descendant selector — all-or-nothing per surface holds.
 */

/** The attribute a surface puts on its root to claim the keyboard. */
export const KEYBOARD_OWNING_SURFACE_ATTR = "data-keyboard-surface";

/**
 * The declared-surface half of the contract. Two selectors, not one, because
 * `.matches()` tests the focused element by itself: the first covers the root
 * holding focus, the second anything focused inside it.
 */
export const KEYBOARD_OWNING_SURFACE_SELECTORS = Object.freeze([
  `[${KEYBOARD_OWNING_SURFACE_ATTR}]`,
  `[${KEYBOARD_OWNING_SURFACE_ATTR}] *`,
]);

/** Text entry: a typed character is never a hotkey. */
const TEXT_ENTRY_SELECTORS = Object.freeze([
  "input",
  "textarea",
  ".core-message-editor",
]);

/**
 * Passed verbatim as `vue-three-shortkey`'s `prevent` option. Every entry is
 * matched against `document.activeElement` alone, never its ancestors.
 */
export const GLOBAL_HOTKEY_AVOID_SELECTORS = Object.freeze([
  ...TEXT_ENTRY_SELECTORS,
  ...KEYBOARD_OWNING_SURFACE_SELECTORS,
]);

/**
 * Does focus currently sit in a surface that has declared it owns the
 * keyboard? The stand-down question for a hand-rolled document listener, asked
 * exactly the way the library asks it — `.matches()` against
 * `document.activeElement` alone, which is why the descendant selector is
 * load-bearing rather than decorative.
 *
 * @returns {boolean} true when a declared surface out-ranks the caller's chord.
 */
export function keyboardOwningSurfaceHasFocus() {
  if (typeof document === "undefined") return false;
  const active = document.activeElement;
  if (!active || typeof active.matches !== "function") return false;
  return KEYBOARD_OWNING_SURFACE_SELECTORS.some((selector) =>
    active.matches(selector),
  );
}
